import { useParams, Link } from "react-router-dom";
import { Phone, ArrowRight, Check } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { SERVICES_DATA } from "@/data/services";
import { Seo } from "@/components/Seo";
import { serviceSeo } from "@/data/seo";

const INCLUDED = ["1 professional mover", "Tail-lift truck", "Moving blankets & straps", "Careful handling", "Free quote before booking"];

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES_DATA.find((s) => s.slug === slug);

  if (!service) {
    return (
      <main className="min-h-screen">
        <Header />
        <div className="mx-auto max-w-3xl px-5 pb-24 pt-48 text-center">
          <h1 className="font-display text-4xl font-semibold text-slate-900">Service not found</h1>
          <Link to="/" className="mt-6 inline-block text-blaze hover:underline">Back to home</Link>
        </div>
        <Footer />
      </main>
    );
  }

  const others = SERVICES_DATA.filter((s) => s.slug !== slug).slice(0, 4);

  return (
    <main className="min-h-screen">
      <Seo {...serviceSeo(service)} />
      <Header />
      <section className="relative overflow-hidden" data-testid="service-page">
        <div className="mx-auto max-w-7xl px-5 pb-20 pt-36 lg:px-10 lg:pt-44">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-blaze">Our Services</p>
              <h1 className="font-display text-4xl font-semibold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl" data-testid="service-title">
                {service.title}
              </h1>
              {service.paragraphs.map((p, i) => (
                <p key={i} className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">{p}</p>
              ))}
              <ul className="mt-7 space-y-2.5">
                {INCLUDED.map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-sm font-medium text-slate-700">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600">
                      <Check className="h-3 w-3 text-white" strokeWidth={3} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/#quote"
                  data-testid="service-quote-btn"
                  className="group flex items-center gap-3 rounded-lg bg-blaze px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:scale-[1.04] hover:bg-blue-700"
                >
                  Get a Free Quote
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <a href="tel:0391136005" className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blaze" data-testid="service-call-link">
                  <Phone className="h-4 w-4 text-blaze" /> 03 9113 6005
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-lg">
                <img src={service.img} alt={service.title} className="h-full w-full object-cover" loading="lazy" />
              </div>
            </Reveal>
          </div>

          <div className="mt-20 border-t border-slate-200 pt-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Explore our other services</p>
            <div className="mt-5 flex flex-wrap gap-2.5" data-testid="service-others">
              {others.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:border-blaze hover:text-blaze"
                >
                  {s.nav}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
